import React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { belongsToUniverse } from "./Universe";
import { usePublicData } from "../lib/publicData";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check, ChevronRight, ChevronLeft, Loader2, Clock, MapPin, Calendar as CalIcon,
  Sparkles, Plus, Bell, X,
} from "lucide-react";
import api from "../lib/api";
import Grain from "../components/Grain";
import { BookingServices } from '../components/booking/BookingServices';
import { BookingActions } from '../components/booking/BookingActions';
import { SiteSettingsContext } from '../components/SiteFeatures';
import '../components/booking/booking.css';
import { BALANCE_PAYMENT_NOTE, depositFor, isPhotoDeposit, paymentAmount } from '../lib/depositTerms';
import { StudioHours } from '../components/StudioHours';

const STEPS = ["Service", "Date & heure", "Vos coordonnées", "Paiement"];

const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const WEEKDAYS_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function formatDateFR(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function Reservation({ sessionsOnly = false, preselectedSlug = '' }) {
  const initial = usePublicData();
  const navigate = useNavigate();
  const { serviceSlug: paramSlug } = useParams();
  const [query] = useSearchParams();
  const requestedSlug = preselectedSlug || paramSlug || query.get('service') || '';
  const settings = React.useContext(SiteSettingsContext);
  const todaySwiss = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const christmasFirst = settings.christmas_enabled === true && Boolean(settings.christmas_end) && todaySwiss <= settings.christmas_end;
  const moduleRef = React.useRef(null);
  const submittingRef = React.useRef(false);
  const [actionsHeight, setActionsHeight] = React.useState(130);
  const scrollToModule = React.useCallback(() => {
    const element = moduleRef.current;
    if (!element) return;
    const headerHeight = document.querySelector('[data-testid="site-header"]')?.getBoundingClientRect().height || 0;
    window.scrollTo({ top: Math.max(0, element.getBoundingClientRect().top + window.scrollY - headerHeight - 16), behavior: 'instant' });
  }, []);
  const universeFilter = query.get('univers');
  const [services, setServices] = React.useState((initial.services || []).filter(s=>!sessionsOnly || (s.category!=='location' && s.category!=='atelier')));
  const [step, setStep] = React.useState(() => services.some(s => s.slug === requestedSlug && !s.requires_quote) ? 1 : 0);
  const [selectedService, setSelectedService] = React.useState(() => services.find(s => s.slug === requestedSlug) || null);
  const [selectedDate, setSelectedDate] = React.useState(() => /^\d{4}-\d{2}-\d{2}$/.test(query.get('date') || '') ? query.get('date') : '');
  const [selectedTime, setSelectedTime] = React.useState("");
  const [calCursor, setCalCursor] = React.useState(() => selectedDate ? new Date(`${selectedDate}T12:00:00`) : new Date());
  const [calStatus, setCalStatus] = React.useState({}); // {YYYY-MM-DD: 'open'|'full'|...}
  const [slots, setSlots] = React.useState([]);
  const [loadingSlots, setLoadingSlots] = React.useState(false);
  const [dateHours, setDateHours] = React.useState(null);
  const [slotsError, setSlotsError] = React.useState('');
  const [loadingCal, setLoadingCal] = React.useState(false);
  const [calendarError, setCalendarError] = React.useState('');
  const [availabilityReload, setAvailabilityReload] = React.useState(0);
  const [client, setClient] = React.useState({ prenom: "", nom: "", email: "", tel: "", notes: "" });
  const [promoCode, setPromoCode] = React.useState("");
  const [promoApplied, setPromoApplied] = React.useState(null); // {code, discount_pct}
  const promoRequest = React.useRef(0);
  const [promoError, setPromoError] = React.useState("");
  const [checkingPromo, setCheckingPromo] = React.useState(false);
  const [addons, setAddons] = React.useState([]); // catalog
  const [selectedAddons, setSelectedAddons] = React.useState({}); // {slug: qty}
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [waitlistOpen, setWaitlistOpen] = React.useState(false);
  const [latePolicyAccepted, setLatePolicyAccepted] = React.useState(false);
  const appliedDeepLink = React.useRef(null);
  const selectService = React.useCallback(service => {
    // Invalidate in-flight price changes before React commits the next service.
    promoRequest.current += 1;
    setPromoApplied(null);
    setPromoCode('');
    setPromoError('');
    setCheckingPromo(false);
    setSelectedAddons({});
    setLatePolicyAccepted(false);
    setSelectedTime('');
    setError('');
    setSelectedService(service);
  }, []);
  React.useEffect(() => { setLatePolicyAccepted(false); }, [selectedService?.slug, selectedService?.late_policy?.version]);

  React.useEffect(() => {
    api.get("/services").then(r => setServices(r.data.filter(s => !sessionsOnly ||
      (s.category !== 'location' && s.category !== 'atelier' && (!universeFilter || belongsToUniverse(s, universeFilter) || (christmasFirst && s.slug === 'mini-noel-2026'))))))
      .catch(() => setError("Impossible de charger les services"));
  }, [sessionsOnly, universeFilter, christmasFirst]);
  React.useEffect(() => {
    setSelectedService(current => current ? services.find(s => s.slug === current.slug) || current : current);
  }, [services]);

  // Deep-link: if URL contains /r/:slug or /reservation/service/:slug, preselect service and jump to step 1
  React.useEffect(() => {
    if (!requestedSlug) { appliedDeepLink.current = null; return; }
    if (!services.length || appliedDeepLink.current === requestedSlug) return;
    const match = services.find(s => s.slug === requestedSlug);
    if (!match) {
      setError("Service introuvable. Choisis parmi la liste ci-dessous.");
      return;
    }
    appliedDeepLink.current = requestedSlug;
    selectService(match);
    if (!selectedDate && match.campaign_start && todaySwiss < match.campaign_start) setCalCursor(new Date(`${match.campaign_start}T12:00:00`));
    if (!match.requires_quote) {
      setStep(1);
      requestAnimationFrame(scrollToModule);
    }
  }, [requestedSlug, services, selectService, selectedDate, todaySwiss, scrollToModule]);

  // Auto-redirect to quote flow for quote-only services
  React.useEffect(() => {
    if (selectedService?.requires_quote) {
      navigate(`/reservation/devis/${selectedService.slug}`);
    }
  }, [selectedService, navigate]);

  // Load addons when service changes
  React.useEffect(() => {
    if (!selectedService || selectedService.requires_quote) return;
    let active = true;
    setAddons([]);
    api.get(`/addons?service_slug=${selectedService.slug}`)
      .then(r => { if (active) setAddons(r.data); })
      .catch(() => { if (active) setAddons([]); });
    return () => { active = false; };
  }, [selectedService]);

  // Load month availability when service or cursor changes
  React.useEffect(() => {
    if (!selectedService) return;
    let active = true;
    const y = calCursor.getFullYear();
    const m = calCursor.getMonth() + 1;
    setLoadingCal(true);
    setCalendarError('');
    setCalStatus({});
    api.get(`/bookings/month-availability?service_slug=${selectedService.slug}&year=${y}&month=${m}`)
      .then(r => { if (active) setCalStatus(r.data); })
      .catch(() => { if (active) { setCalStatus({}); setCalendarError('Impossible de charger le calendrier. Réessaie.'); } })
      .finally(() => { if (active) setLoadingCal(false); });
    return () => { active = false; };
  }, [selectedService, calCursor, availabilityReload]);

  // Load slots when date changes
  React.useEffect(() => {
    if (!selectedService || !selectedDate) return;
    let active = true;
    setLoadingSlots(true);
    setSelectedTime("");
    setSlots([]); setDateHours(null); setSlotsError('');
    api.get(`/bookings/availability?service_slug=${selectedService.slug}&date=${selectedDate}`)
      .then(r => { if (active) { setSlots(r.data.slots || []); setDateHours(r.data.studio_hours); } })
      .catch(() => { if (active) { setSlots([]); setSlotsError('Impossible de charger les disponibilités. Sélectionne à nouveau une date.'); } })
      .finally(() => { if (active) setLoadingSlots(false); });
    return () => { active = false; };
  }, [selectedService, selectedDate, availabilityReload]);

  const stepError = current => {
    if (current === 0 && !selectedService) return 'Choisis une formule pour continuer.';
    if (current === 1) {
      if (!selectedDate) return 'Choisis une date pour continuer.';
      if (loadingSlots) return 'Les créneaux sont en cours de chargement.';
      if (!selectedTime || !slots.includes(selectedTime)) return 'Choisis un horaire disponible pour continuer.';
    }
    if (current === 2) {
      if (!client.prenom.trim()) return 'Indique ton prénom pour continuer.';
      if (!client.nom.trim()) return 'Indique ton nom pour continuer.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email.trim())) return 'Indique une adresse e-mail valide pour continuer.';
      if (!client.tel.trim()) return 'Indique ton téléphone pour continuer.';
    }
    return '';
  };

  const next = () => {
    scrollToModule();
    const message = stepError(step);
    setError(message);
    if (message) return;
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };
  const prev = () => { setError(''); scrollToModule(); setStep(s => Math.max(s - 1, 0)); };

  React.useEffect(() => {
    promoRequest.current += 1;
    setPromoApplied(null);
    setPromoCode("");
    setPromoError("");
    setCheckingPromo(false);
  }, [selectedService?.slug]);

  const promosAllowed = selectedService?.promo_codes_allowed !== false && selectedService?.slug !== 'mini-noel-2026';
  const basePrice = selectedService?.price_chf || 0;
  const servicePrice = promosAllowed && promoApplied && !promoApplied.gift
    ? Math.max(1, Math.round(basePrice * (100 - promoApplied.discount_pct) / 100))
    : basePrice;
  const addonsTotal = React.useMemo(() => {
    return addons.reduce((sum, a) => sum + (selectedAddons[a.slug] || 0) * a.price_chf, 0);
  }, [addons, selectedAddons]);
  const effectivePrice = servicePrice + addonsTotal;
  const giftCredit = promoApplied?.gift ? (promoApplied.monetary ? Math.min(effectivePrice, promoApplied.balance) : basePrice) : 0;
  const depositAmount = selectedService
    ? Math.max(0, depositFor(selectedService, effectivePrice) - giftCredit)
    : 0;
  const remainingAmount = selectedService ? effectivePrice - depositAmount - giftCredit : 0;

  const applyPromo = async () => {
    if (!promoCode.trim() || !selectedService) { setPromoError('Saisis un code pour l’appliquer.'); return; }
    setCheckingPromo(true);
    setPromoError("");
    const codeUp = promoCode.trim().toUpperCase();
    const isGift = codeUp.startsWith("GIFT-");
    const requestId = ++promoRequest.current;
    if (!promosAllowed && !isGift) {
      setPromoApplied(null);
      setPromoError("Le Shooting de Noël est au tarif fixe de CHF 110, sans code promo.");
      setCheckingPromo(false);
      return;
    }
    try {
      if (isGift) {
        const r = await api.post("/gifts/validate", {
          code: codeUp,
          service_slug: selectedService.slug,
        });
        if (requestId !== promoRequest.current) return;
        // A prepaid gift is a payment credit, not a promotional discount.
        setPromoApplied({ code: r.data.code, discount_pct: 100, gift: true, monetary: r.data.monetary, balance: r.data.balance_chf });
      } else {
        const r = await api.post("/promo/validate", {
          code: codeUp,
          service_slug: selectedService.slug,
        });
        if (requestId !== promoRequest.current) return;
        setPromoApplied({ code: r.data.code, discount_pct: r.data.discount_pct, gift: false });
      }
    } catch (e) {
      if (requestId !== promoRequest.current) return;
      setPromoApplied(null);
      setPromoError(e?.response?.data?.detail || "Code invalide");
    } finally {
      if (requestId === promoRequest.current) setCheckingPromo(false);
    }
  };

  const clearPromo = () => {
    promoRequest.current += 1;
    setPromoApplied(null);
    setPromoCode("");
    setPromoError("");
  };

  const submit = async () => {
    if (submittingRef.current || checkingPromo) return;
    for (const current of [0, 1, 2]) {
      const message = stepError(current);
      if (message) { setError(message); setStep(current); scrollToModule(); return; }
    }
    if (selectedService?.late_policy && !latePolicyAccepted) {
      setError("Coche les conditions pour continuer.");
      return;
    }
    setError("");
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const r = await api.post("/bookings", {
        service_slug: selectedService.slug,
        late_policy_accepted: latePolicyAccepted,
        late_policy_version: selectedService.late_policy?.version,
        date: selectedDate,
        start_time: selectedTime,
        client: {
          prenom: client.prenom.trim(),
          nom: client.nom.trim(),
          email: client.email.trim().toLowerCase(),
          tel: client.tel.trim(),
        },
        notes: client.notes.trim(),
        promo_code: promosAllowed && promoApplied && !promoApplied.gift ? promoApplied.code : "",
        gift_code: promoApplied && promoApplied.gift ? promoApplied.code : "",
        addons: Object.entries(selectedAddons)
          .filter(([, qty]) => qty > 0)
          .map(([slug, qty]) => ({ slug, qty })),
        origin_url: origin,
      });
      // Redirect to Stripe
      window.location.href = r.data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : "Impossible de finaliser la réservation. Vérifie tes informations et réessaie.";
      setError(msg);
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="booking-page section-dark min-h-screen pb-20 relative" style={{ '--booking-actions-height': `${actionsHeight}px` }}>
      <Grain />
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 relative z-20">
        <div className="eyebrow mb-4" data-testid="reservation-eyebrow">Réservation en ligne</div>
        <h1 className="font-serif text-5xl md:text-6xl mb-4" data-testid="reservation-title">
          Réserve ta <span className="italic-accent">séance</span>
        </h1>
        <p className="text-text-cream/70 max-w-2xl mb-6 font-light leading-relaxed">
          Un seul lien, tous les shootings — portrait, maternité, famille, branding, location studio.
          Confirmation immédiate, calendrier à jour en temps réel.
        </p>
        <div className="mb-10">
          <Link
            to={selectedService ? `/offrir/${selectedService.slug}` : "/offrir"}
            className="inline-flex items-center gap-2 text-sm text-accent-light hover:text-accent-deep transition border-b border-accent-deep/40 hover:border-accent-deep pb-1"
            data-testid="offrir-cta-link"
          >
            🎁 Plutôt offrir cette séance à un proche ?
          </Link>
        </div>

        {/* Stepper */}
        <div ref={moduleRef} id="booking-module" className="booking-module flex items-center gap-2 md:gap-4 mb-8 pb-2" data-testid="reservation-stepper">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div aria-current={i === step ? 'step' : undefined} aria-label={`Étape ${i + 1} : ${label}`} data-testid={`booking-step-${i + 1}`} className={`flex items-center gap-3 min-w-fit ${i === step ? "text-accent-light" : i < step ? "text-text-cream/80" : "text-text-cream/30"}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm border ${i === step ? "border-accent-deep bg-accent-deep/10" : i < step ? "border-accent-light/50 bg-accent-light/10" : "border-white/15"}`}>
                  {i < step ? <Check size={14} /> : i + 1}
                </div>
                <span className="font-sans text-[0.75rem] uppercase tracking-[0.2em] hidden md:inline">{label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-px flex-1 min-w-[20px] ${i < step ? "bg-accent-light/40" : "bg-white/10"}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <AnimatePresence mode="wait" onExitComplete={scrollToModule}>
          {step === 0 && (
            <motion.div
              key="step-0"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
            >
              <BookingServices
                services={services}
                selectedService={selectedService}
                setSelectedService={selectService}
                christmasFirst={christmasFirst}
              />
            </motion.div>
          )}

          {step === 1 && selectedService && (
            <motion.div
              key="step-1"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              className="grid md:grid-cols-[1.4fr_1fr] gap-8"
            >
              <div>
                <h2 className="font-serif text-2xl mb-4">Choisis une date</h2>
                <details className="mb-4 text-sm text-text-cream/70"><summary className="cursor-pointer" data-testid="booking-studio-hours-toggle">Horaires du studio</summary><StudioHours id="reservation" compact/></details>
                {calendarError && <div className="text-sm text-red-300 mb-3" role="alert" data-testid="booking-calendar-error">{calendarError}<button type="button" className="underline ml-2" onClick={() => setAvailabilityReload(value => value + 1)} data-testid="booking-calendar-retry">Réessayer</button></div>}
                <Calendar
                  cursor={calCursor}
                  setCursor={setCalCursor}
                  status={calStatus}
                  loading={loadingCal}
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                />
              </div>
              <div>
                <h2 className="font-serif text-2xl mb-4">Créneau horaire</h2>
                {!selectedDate && (
                  <p className="text-text-cream/50 text-sm italic font-light">Sélectionne d'abord une date dans le calendrier.</p>
                )}
                {selectedDate && (
                  <div data-testid="slots-container">
                    <p className="eyebrow text-accent-light mb-3">{formatDateFR(selectedDate)}</p>
                    {!loadingSlots && !slotsError && <p className="text-sm mb-4 text-white/80" data-testid="selected-day-hours">{dateHours ? `Studio ouvert de ${dateHours.start_time} à ${dateHours.end_time} · heure suisse` : 'Studio fermé ce jour'}</p>}
                    {slotsError && <div role="alert" data-testid="slots-error" className="text-sm text-rose-300 mb-4">{slotsError}<button type="button" className="underline ml-2" data-testid="booking-slots-retry" onClick={() => setAvailabilityReload(value => value + 1)}>Réessayer</button></div>}
                    {loadingSlots && <Loader2 className="animate-spin text-accent-deep" size={22} />}
                    {!loadingSlots && !slotsError && slots.length === 0 && (
                      <div className="space-y-3" data-testid="no-slots-block">
                        <p className="text-text-cream/60 text-sm">{dateHours ? 'Aucun créneau disponible pour cette durée. Choisis une autre date ou inscris-toi sur la liste d’attente.' : 'Choisis un jour d’ouverture dans le calendrier.'}</p>
                        {dateHours && <button
                          type="button"
                          onClick={() => setWaitlistOpen(true)}
                          className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded border border-accent-deep/40 bg-accent-deep/10 text-accent-light hover:bg-accent-deep/20 transition"
                          data-testid="open-waitlist-btn"
                        >
                          <Bell size={14} /> M'inscrire sur la liste d'attente
                        </button>}
                      </div>
                    )}
                    {!loadingSlots && slots.length > 0 && (
                      <div className="grid grid-cols-3 gap-2">
                        {slots.map(t => (
                          <button
                            key={t}
                            data-testid={`slot-${t}`}
                            onClick={() => setSelectedTime(t)}
                            className={`py-3 text-sm border rounded transition-all ${
                              selectedTime === t
                                ? "bg-accent-deep text-white border-accent-deep"
                                : "border-white/15 text-text-cream/80 hover:border-accent-deep hover:text-accent-light"
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedTime && (
                      <div className="mt-4 p-3 bg-accent-deep/10 border border-accent-deep/30 rounded text-sm" data-testid="booking-estimated-end">
                        <Clock size={14} className="inline mr-2 text-accent-light" />
                        Fin estimée : <strong className="text-accent-light">{addMinutes(selectedTime, selectedService.duration_min)}</strong> ({selectedService.duration_min} min)
                      </div>
                    )}
                    {!loadingSlots && slots.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setWaitlistOpen(true)}
                        className="mt-6 text-xs text-text-cream/50 hover:text-accent-light transition inline-flex items-center gap-2"
                        data-testid="open-waitlist-link"
                      >
                        <Bell size={12} /> Date préférée pas affichée ? Rejoins la liste d'attente
                      </button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step-2"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              className="max-w-2xl"
            >
              <h2 className="font-serif text-2xl mb-6">Tes coordonnées</h2>
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Prénom" autoComplete="given-name" value={client.prenom} onChange={v => { setClient({ ...client, prenom: v }); setError(''); }} testid="input-prenom" />
                <Field label="Nom" autoComplete="family-name" value={client.nom} onChange={v => { setClient({ ...client, nom: v }); setError(''); }} testid="input-nom" />
                <Field label="Email" type="email" autoComplete="email" value={client.email} onChange={v => { setClient({ ...client, email: v }); setError(''); }} testid="input-email" />
                <Field label="Téléphone" type="tel" autoComplete="tel" value={client.tel} onChange={v => { setClient({ ...client, tel: v }); setError(''); }} testid="input-tel" placeholder="+41 ..." />
              </div>
              <div className="mt-5">
                <label className="block text-text-cream/60 text-xs uppercase tracking-[0.2em] mb-2">
                  Message (optionnel)
                </label>
                <textarea
                  data-testid="input-notes"
                  aria-label="Notes pour la séance"
                  rows={3}
                  value={client.notes}
                  onChange={e => setClient({ ...client, notes: e.target.value })}
                  placeholder="Idées, inspirations, besoins particuliers..."
                  className="w-full bg-white/5 border border-white/15 rounded px-4 py-3 text-text-cream placeholder:text-text-cream/30 focus:outline-none focus:border-accent-deep font-light"
                />
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step-3"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              className="max-w-2xl"
            >
              <h2 className="font-serif text-2xl mb-6">Récapitulatif & paiement</h2>
              <Summary service={selectedService} date={selectedDate} time={selectedTime} client={client} />

              {/* Add-ons */}
              {addons.length > 0 && (
                <div className="mt-6 p-5 bg-white/[0.02] border border-white/10 rounded" data-testid="addons-section">
                  <div className="eyebrow mb-1 text-text-cream/60">Options & extras</div>
                  <p className="text-xs text-text-cream/40 font-light mb-4">Boost ta séance (optionnel)</p>
                  <div className="space-y-3">
                    {addons.map(a => {
                      const qty = selectedAddons[a.slug] || 0;
                      const isCheckbox = a.max_qty === 1;
                      return (
                        <div
                          key={a.slug}
                          className={`p-3 border rounded transition-all ${qty > 0 ? "border-accent-deep/40 bg-accent-deep/5" : "border-white/10"}`}
                          data-testid={`addon-${a.slug}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="font-serif text-base">{a.name}</div>
                              <div className="text-xs text-text-cream/50 font-light mt-0.5 leading-relaxed">{a.description}</div>
                              <div className="text-sm text-accent-light mt-1.5">CHF {a.price_chf}{a.unit_label || ""}</div>
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-2">
                              {isCheckbox ? (
                                <button
                                  type="button"
                                  onClick={() => setSelectedAddons(prev => ({...prev, [a.slug]: qty ? 0 : 1}))}
                                  className={`w-10 h-10 rounded border flex items-center justify-center transition ${qty ? "bg-accent-deep border-accent-deep text-white" : "border-white/20 text-text-cream/40 hover:border-accent-deep"}`}
                                  data-testid={`addon-toggle-${a.slug}`}
                                >
                                  {qty ? <Check size={16}/> : <Plus size={16}/>}
                                </button>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => setSelectedAddons(prev => ({...prev, [a.slug]: Math.max(0, qty - 1)}))}
                                    disabled={qty === 0}
                                    className="w-8 h-8 rounded border border-white/15 text-text-cream/60 flex items-center justify-center hover:border-accent-deep disabled:opacity-30 disabled:cursor-not-allowed"
                                    data-testid={`addon-dec-${a.slug}`}
                                  >−</button>
                                  <span className="w-7 text-center font-serif text-base" data-testid={`addon-qty-${a.slug}`}>{qty}</span>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedAddons(prev => ({...prev, [a.slug]: Math.min(a.max_qty, qty + 1)}))}
                                    disabled={qty >= a.max_qty}
                                    className="w-8 h-8 rounded border border-white/15 text-text-cream/60 flex items-center justify-center hover:border-accent-deep disabled:opacity-30 disabled:cursor-not-allowed"
                                    data-testid={`addon-inc-${a.slug}`}
                                  >+</button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {addonsTotal > 0 && (
                    <div className="mt-4 pt-3 border-t border-white/10 flex justify-between text-sm" data-testid="addons-total">
                      <span className="text-text-cream/60">Sous-total options</span>
                      <span className="text-accent-light font-serif text-lg">+ CHF {addonsTotal}.00</span>
                    </div>
                  )}
                </div>
              )}

              {/* Promo code */}
              <div className="mt-6 p-5 bg-white/[0.02] border border-white/10 rounded">
                <div className="eyebrow mb-3 text-text-cream/60" data-testid="promo-field-label">{promosAllowed ? 'Code promo ou bon cadeau' : 'Bon cadeau'}</div>
                {!promosAllowed && <p className="text-sm text-text-cream/70 mb-3" data-testid="christmas-no-promo-notice">Tarif fixe CHF 110, sans code promo. Un bon cadeau prépayé reste un moyen de paiement.</p>}
                {promoApplied ? (
                  <div className="flex items-center justify-between" data-testid="promo-applied">
                    <div>
                      <span className="font-serif text-lg text-accent-light">{promoApplied.code}</span>
                      <span className="ml-3 text-sm text-text-cream/60" data-testid="promo-applied-value">{promoApplied.gift ? `Bon cadeau : CHF ${giftCredit}.00` : `–${promoApplied.discount_pct} % appliquée`}</span>
                    </div>
                    <button type="button" onClick={clearPromo} className="text-xs text-text-cream/50 hover:text-red-300" data-testid="clear-promo">
                      Retirer
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      data-testid="promo-input"
                      aria-label={promosAllowed ? "Code promotionnel ou bon cadeau" : "Bon cadeau prépayé"}
                      type="text"
                      value={promoCode}
                      onChange={e => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Entrez votre code"
                      className="flex-1 min-w-0 bg-white/5 border border-white/15 rounded px-3 py-2 text-sm uppercase tracking-wider"
                    />
                    <button
                      type="button"
                      data-testid="apply-promo-btn"
                      onClick={applyPromo}
                      disabled={checkingPromo}
                      className="btn-ghost text-xs disabled:opacity-40"
                    >
                      {checkingPromo ? <Loader2 className="animate-spin" size={14}/> : "Appliquer"}
                    </button>
                  </div>
                )}
                {promoError && !promoApplied && (
                  <div className="mt-2 text-xs text-red-300" data-testid="promo-error">{promoError}</div>
                )}
              </div>

              <div className="mt-6 p-6 bg-white/5 border border-white/15 rounded">
                {promosAllowed && promoApplied && !promoApplied.gift && (
                  <div className="flex items-center justify-between mb-2 text-sm">
                    <span className="text-text-cream/50 line-through">Prix catalogue</span>
                    <span className="text-text-cream/40 line-through">CHF {basePrice}.00</span>
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-text-cream/60 text-sm">Montant total {promosAllowed && promoApplied && !promoApplied.gift && <span className="text-accent-light">(après remise)</span>}</span>
                  <span className="font-serif text-xl" data-testid="total-amount">CHF {effectivePrice}.00</span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-text-cream/60 text-sm">
                    {selectedService?.deposit_pct === 100 ? "Payable en ligne (intégral)" : "Acompte à payer maintenant"}
                  </span>
                  <span className="font-serif text-2xl text-accent-light" data-testid="deposit-amount">CHF {paymentAmount(depositAmount)}</span>
                </div>
                {remainingAmount > 0 && isPhotoDeposit(selectedService) && <p className="text-xs text-text-cream/70 mb-3 leading-relaxed" data-testid="balance-payment-note">{BALANCE_PAYMENT_NOTE}</p>}
                {remainingAmount > 0 && (
                  <div className="flex items-center justify-between pt-3 border-t border-white/10 text-sm text-text-cream/60">
                    <span>Reste à régler sur place</span>
                    <span data-testid="remaining-amount">CHF {paymentAmount(remainingAmount)}</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-text-cream/50 mt-4 leading-relaxed">
                Paiement sécurisé via Stripe. Tu seras redirigé(e) sur une page sécurisée pour saisir ta carte.
                Une confirmation + ton créneau dans ton calendrier (.ics) te sera envoyé immédiatement par email.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <BookingActions step={step} service={selectedService} date={selectedDate} time={selectedTime} total={effectivePrice} deposit={depositAmount} policy={selectedService?.late_policy} accepted={latePolicyAccepted} onAccept={value => { setLatePolicyAccepted(value); setError(''); }} busy={submitting} checkingPromo={checkingPromo} error={error} next={next} prev={prev} pay={submit} onHeight={setActionsHeight}/>
      </div>

      {selectedService && (
        <WaitlistModal
          open={waitlistOpen}
          onClose={() => setWaitlistOpen(false)}
          service={selectedService}
          defaultDate={selectedDate}
        />
      )}
    </div>
  );
}

function WaitlistModal({ open, onClose, service, defaultDate = "" }) {
  const [form, setForm] = React.useState({ prenom: "", nom: "", email: "", tel: "", notes: "" });
  const [date, setDate] = React.useState(defaultDate || "");
  const [anyDate, setAnyDate] = React.useState(!defaultDate);
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setDone(false);
      setErr("");
      setDate(defaultDate || "");
      setAnyDate(!defaultDate);
    }
  }, [open, defaultDate]);

  if (!open) return null;

  const valid = form.prenom.trim() && /^[^@]+@[^@]+\.[^@]+$/.test(form.email) && (anyDate || date);

  const submit = async () => {
    if (submitting) return;
    if (!valid) { setErr(!form.prenom.trim() ? 'Indique ton prénom pour continuer.' : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) ? 'Indique une adresse e-mail valide.' : 'Choisis une date ou coche Toutes dates.'); return; }
    setSubmitting(true);
    setErr("");
    try {
      await api.post("/waitlist", {
        service_slug: service.slug,
        date: anyDate ? "" : date,
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        email: form.email.trim().toLowerCase(),
        tel: form.tel.trim(),
        notes: form.notes.trim(),
      });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Impossible de t'inscrire. Réessaie.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="waitlist-modal"
    >
      <div
        className="relative w-full max-w-md bg-dark border border-accent-deep/30 rounded-lg p-6 md:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-text-cream/50 hover:text-accent-light transition"
          data-testid="waitlist-close"
          aria-label="Fermer"
        >
          <X size={20} />
        </button>

        {done ? (
          <div className="text-center py-4" data-testid="waitlist-done">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent-light/15 text-accent-light mb-4">
              <Check size={22} />
            </div>
            <h3 className="font-serif text-2xl text-text-cream mb-2">Inscription confirmée</h3>
            <p className="text-text-cream/60 text-sm font-light mb-6">
              Je t'écris dès qu'un créneau se libère pour <strong className="text-accent-light">{service.name}</strong>.
              Le premier à confirmer le paiement garde la place.
            </p>
            <button
              onClick={onClose}
              className="btn-primary"
              data-testid="waitlist-done-close"
            >
              Compris
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent-deep/20 text-accent-light">
                <Bell size={18} />
              </span>
              <div>
                <div className="eyebrow text-accent-deep">Liste d'attente</div>
                <h3 className="font-serif text-xl text-text-cream">{service.name}</h3>
              </div>
            </div>
            <p className="text-text-cream/60 text-sm font-light mb-5">
              Je te préviens par email dès qu'un créneau se libère. Zéro spam, juste cette alerte.
            </p>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Prénom *"
                  value={form.prenom}
                  onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2.5 text-sm text-text-cream focus:border-accent-deep outline-none"
                  data-testid="waitlist-prenom"
                  autoComplete="given-name"
                />
                <input
                  type="text"
                  placeholder="Nom"
                  value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2.5 text-sm text-text-cream focus:border-accent-deep outline-none"
                  data-testid="waitlist-nom"
                  autoComplete="family-name"
                />
              </div>
              <input
                type="email"
                placeholder="Email *"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2.5 text-sm text-text-cream focus:border-accent-deep outline-none"
                data-testid="waitlist-email"
                autoComplete="email"
              />
              <input
                type="tel"
                placeholder="Téléphone (optionnel)"
                value={form.tel}
                onChange={(e) => setForm({ ...form, tel: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2.5 text-sm text-text-cream focus:border-accent-deep outline-none"
                data-testid="waitlist-tel"
                autoComplete="tel"
              />

              <div className="pt-2">
                <label className="flex items-center gap-2 text-sm text-text-cream/70 font-light cursor-pointer">
                  <input
                    type="checkbox"
                    checked={anyDate}
                    onChange={(e) => setAnyDate(e.target.checked)}
                    className="accent-accent-deep"
                    data-testid="waitlist-anydate"
                  />
                  Toute date possible — je m'adapte
                </label>
                {!anyDate && (
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2.5 text-sm text-text-cream focus:border-accent-deep outline-none"
                    data-testid="waitlist-date"
                  />
                )}
              </div>

              <textarea
                placeholder="Préférences, créneaux, questions (optionnel)"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2.5 text-sm text-text-cream focus:border-accent-deep outline-none resize-none"
                data-testid="waitlist-notes"
              />
            </div>

            {err && (
              <p className="text-rose-300 text-xs mt-3" data-testid="waitlist-error">{err}</p>
            )}

            <button
              onClick={submit}
              disabled={submitting}
              className="btn-primary w-full mt-5 disabled:opacity-50"
              data-testid="waitlist-submit"
            >
              {submitting ? (
                <><Loader2 className="animate-spin mr-2 inline" size={14} /> Inscription…</>
              ) : (
                <>M'inscrire sur la liste</>
              )}
            </button>
            <p className="text-text-cream/40 text-[0.65rem] mt-3 text-center font-light">
              En t'inscrivant, tu acceptes de recevoir un email de notification. Pas de newsletter.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Calendar({ cursor, setCursor, status, loading, selected, onSelect }) {
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const firstWeekday = (first.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => setCursor(new Date(y, m - 1, 1));
  const nextMonth = () => setCursor(new Date(y, m + 1, 1));

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded p-5" data-testid="calendar">
      <div className="flex items-center justify-between mb-4">
        <button type="button" aria-label="Mois précédent" onClick={prevMonth} className="text-text-cream/60 hover:text-accent-light p-1" data-testid="cal-prev">
          <ChevronLeft size={18} />
        </button>
        <div className="font-serif text-lg" data-testid="calendar-month">{MONTHS_FR[m]} {y}</div>
        <button type="button" aria-label="Mois suivant" onClick={nextMonth} className="text-text-cream/60 hover:text-accent-light p-1" data-testid="cal-next">
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEKDAYS_FR.map(d => (
          <div key={d} className="text-center text-text-cream/40 text-[0.65rem] uppercase tracking-wider py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-dark/60 z-10" role="status" aria-label="Chargement du calendrier" data-testid="calendar-loading">
            <Loader2 className="animate-spin text-accent-deep" size={22} />
          </div>
        )}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const iso = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const st = status[iso];
          const isSelected = selected === iso;
          const disabled = !st || st === "past" || st === "closed" || st === "full";
          return (
            <button
              key={i}
              data-testid={`cal-day-${iso}`}
              aria-label={formatDateFR(iso)}
              aria-pressed={isSelected}
              onClick={() => !disabled && onSelect(iso)}
              disabled={disabled}
              className={`aspect-square flex flex-col items-center justify-center text-sm rounded transition-all ${
                isSelected
                  ? "bg-accent-deep text-white"
                  : disabled
                  ? "text-text-cream/20 cursor-not-allowed"
                  : "text-text-cream hover:bg-accent-deep/20 hover:text-accent-light"
              }`}
            >
              <span>{d}</span>
              {st === "open" && !isSelected && <span className="w-1 h-1 rounded-full bg-accent-light mt-0.5" />}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10 text-[0.65rem] text-text-cream/40 flex-wrap">
        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-accent-light" /> Disponible</span>
        <span>Horaires variables selon le jour</span>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", testid, autoComplete }) {
  return (
    <div>
      <label htmlFor={testid} className="block text-text-cream/60 text-xs uppercase tracking-[0.2em] mb-2">{label}</label>
      <input
        id={testid}
        data-testid={testid}
        type={type}
        autoComplete={autoComplete}
        name={autoComplete}
        required
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/15 rounded px-4 py-3 text-text-cream placeholder:text-text-cream/30 focus:outline-none focus:border-accent-deep font-light"
      />
    </div>
  );
}

function Summary({ service, date, time, client }) {
  const endTime = addMinutes(time, service?.duration_min || 0);
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded p-6 space-y-4" data-testid="summary">
      <Row icon={<Sparkles size={14}/>} label="Service" value={service?.name} />
      <Row icon={<CalIcon size={14}/>} label="Date" value={formatDateFR(date)} />
      <Row icon={<Clock size={14}/>} label="Horaire" value={`${time} – ${endTime}`} />
      <Row icon={<MapPin size={14}/>} label="Lieu" value="Studio Euclides, Route de Crochy 20, 1024 Écublens" />
      <div className="pt-4 border-t border-white/10 text-sm">
        <div className="text-text-cream/80 break-words" data-testid="booking-summary-client">{client.prenom} {client.nom}</div>
        <div className="text-text-cream/50 text-xs break-words" data-testid="booking-summary-contact">{client.email} · {client.tel}</div>
      </div>
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex gap-4">
      <div className="w-7 h-7 rounded-full bg-accent-deep/15 flex items-center justify-center text-accent-light flex-shrink-0">{icon}</div>
      <div className="flex-1">
        <div className="eyebrow text-text-cream/40 mb-0.5">{label}</div>
        <div className="text-text-cream font-light" data-testid={`booking-summary-${label.toLowerCase()}`}>{value}</div>
      </div>
    </div>
  );
}

function addMinutes(hhmm, minutes) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + (minutes || 0);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function formatDuration(mins) {
  if (!mins) return "";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} h` : `${h} h ${m}`;
}
